// Backend routing: non-production hosts must never reach the production
// backend. A human or agent opening a preview link is testing, and prod writes
// from a preview are real writes. Preview hosts default to the always-on dev
// backend; `?backendPr=<N>` selects a specific krece-backend PR preview and
// `?backendPr=dev` goes back to the shared dev backend. The override is a PR
// *number*, never a raw URL — so a crafted link can't route tokens to an
// arbitrary host — and it is ignored on production domains.
//
// Detection is by preview-host SUFFIX, so any production domain (renamed or
// new) keeps talking to prod without a code change. The tradeoff: previews
// hosted on a domain that is NOT listed here would silently reach production,
// so a new preview host must be added below. The Amplify host
// (dev.d27f6wcfvq3ur.amplifyapp.com) is deliberately NOT a preview: it runs
// fully on production (prod backend + prod Firebase) by product decision.
const HARDCODED_PROD_PREFIX = 'https://api.krece.app';
const PROD_API_ORIGIN = trimTrailingSlashes(process.env.REACT_APP_API_URL || HARDCODED_PROD_PREFIX);
const DEV_API_ORIGIN = 'https://krece-backend-development.up.railway.app';
const PREVIEW_HOST_SUFFIXES = ['.vercel.app'];
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const BACKEND_PR_STORAGE_KEY = 'kreceBackendPr';
const BACKEND_PR_PATTERN = /^\d{1,6}$/;
const DEV_BACKEND_SENTINEL = 'dev';

function trimTrailingSlashes(url: string): string {
	return url.replace(/\/+$/, '');
}

function isValidOverride(value: string | null): boolean {
	return !!value && (value === DEV_BACKEND_SENTINEL || BACKEND_PR_PATTERN.test(value));
}

// Returns the active override: a PR number, the dev-backend sentinel, or null.
// A valid `?backendPr` wins, followed by `#backendPr` as a redirect-safe fallback,
// and is remembered for the tab. Anything invalid is ignored so a typo can't
// silently change which backend a session talks to.
export function backendPrOverride(): string | null {
	try {
		const fromUrl = new URLSearchParams(window.location.search).get('backendPr');
		const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('backendPr');
		const requestedOverride = isValidOverride(fromUrl) ? fromUrl : fromHash;
		if (isValidOverride(requestedOverride)) {
			try {
				sessionStorage.setItem(BACKEND_PR_STORAGE_KEY, requestedOverride as string);
			} catch {
				// Embedded browsers may deny storage; the explicit URL still selects the backend.
			}
			return requestedOverride;
		}
		const stored = sessionStorage.getItem(BACKEND_PR_STORAGE_KEY);
		return isValidOverride(stored) ? stored : null;
	} catch {
		return null;
	}
}

function isPreviewHost(): boolean {
	if (typeof window === 'undefined' || !window.location) return false;
	const { hostname } = window.location;
	return PREVIEW_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}

function isLocalHost(): boolean {
	if (typeof window === 'undefined' || !window.location) return false;
	return LOCAL_HOSTS.includes(window.location.hostname);
}

function resolveApiOrigin(): string {
	if (typeof window === 'undefined' || !window.location) return PROD_API_ORIGIN;
	const preview = isPreviewHost();
	if (!preview && !isLocalHost()) return PROD_API_ORIGIN;
	const override = backendPrOverride();
	if (override && override !== DEV_BACKEND_SENTINEL) {
		return `https://krece-backend-krece-backend-pr-${override}.up.railway.app`;
	}
	if (override === DEV_BACKEND_SENTINEL) return DEV_API_ORIGIN;
	return preview ? DEV_API_ORIGIN : PROD_API_ORIGIN;
}

export const API_ORIGIN = resolveApiOrigin();

// Preview hosts live entirely in the dev world: they sign into the krece-dev
// Firebase project (auth, Firestore, storage) to match the dev backend they
// already talk to — one coherent environment, and prod data is unreachable.
// Localhost is included for the same reason: resolveApiOrigin() already sends a
// local dev server to the dev backend, so leaving Firebase on production gave a
// half-and-half environment that reads prod Firestore for records the dev
// backend just wrote elsewhere. Production domains keep the env-provided prod
// project.
// `?backendPr` never affects this choice; it only swaps the REST base.
const PROD_FIREBASE_CONFIG = {
	apiKey: process.env.REACT_APP_FIREBASE_KEY,
	authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FB_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FB_APP_ID,
	measurementId: process.env.REACT_APP_FB_MEASUREMENT_ID
};

const DEV_FIREBASE_CONFIG = {
	apiKey: 'AIzaSyBjw10fBmsz3orBjyKTJHxUwKgWC0NAIEM',
	authDomain: 'krece-dev.firebaseapp.com',
	projectId: 'krece-dev',
	storageBucket: 'krece-dev.firebasestorage.app',
	messagingSenderId: '430955411758',
	appId: '1:430955411758:web:ea79c5a1a8a8e1aaf6e2bf'
};

export const FIREBASE_CONFIG = isPreviewHost() || isLocalHost() ? DEV_FIREBASE_CONFIG : PROD_FIREBASE_CONFIG;

export function toApiUrl(endpointOrUrl: string): string {
	if (endpointOrUrl.startsWith(HARDCODED_PROD_PREFIX)) {
		return `${API_ORIGIN}/${endpointOrUrl.slice(HARDCODED_PROD_PREFIX.length).replace(/^\/+/, '')}`;
	}
	if (/^https?:\/\//.test(endpointOrUrl)) return endpointOrUrl;
	return `${API_ORIGIN}/${endpointOrUrl.replace(/^\/+/, '')}`;
}
